// MeetParticipantTile — renders one participant in the VideoRoom grid.
// Uses a real <video> element; on render attaches the participant's video
// track via livekit-client's track.attach() helper. The live Room instance
// comes from the connectCall module (NOT from state — smbls strips methods
// when objects go through the reactive proxy).
import { getLivekitRoom } from '../../functions/meet/connectCall.js'

// Helper to iterate a participant's track publications. LiveKit's
// `videoTrackPublications` / `audioTrackPublications` may be a Map or a
// plain object depending on version — handle both.
const _iterPubs = (obj) => {
  if (!obj) return []
  if (typeof obj.values === 'function') return [...obj.values()]
  return Object.values(obj)
}

// Attach a track once. track.attach() reassigns srcObject with a fresh
// MediaStream every call, which causes visible flicker when onRender runs
// repeatedly. Tag the element with the last-attached trackSid and bail on
// matching calls. Detach + re-attach only when the track actually changes
// (e.g. participant republishes after a mute toggle).
const _attachVideoTrack = (videoEl, room, identity, isLocal) => {
  if (!videoEl || !room) return
  const participant = isLocal
    ? room.localParticipant
    : (typeof room.remoteParticipants?.get === 'function'
        ? room.remoteParticipants.get(identity)
        : room.remoteParticipants?.[identity])
  if (!participant) return

  let targetPub = null
  for (const pub of _iterPubs(participant.videoTrackPublications)) {
    if (pub.track && (pub.source === 'camera' || pub.kind === 'video')) {
      targetPub = pub
      break
    }
  }

  const currentSid = videoEl._lkTrackSid || null
  const targetSid = targetPub?.trackSid || null

  if (currentSid === targetSid) return // already attached to this exact track

  // Track changed (or removed). Detach the old and attach the new.
  if (videoEl._lkTrack) {
    try { videoEl._lkTrack.detach(videoEl) } catch { /* ignore */ }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(videoEl)
      videoEl._lkTrack = targetPub.track
      videoEl._lkTrackSid = targetSid
    } catch (err) { console.warn('[tile] video attach failed', err) }
  } else {
    videoEl._lkTrack = null
    videoEl._lkTrackSid = null
  }
}

const _attachAudioTrack = (audioEl, room, identity, isLocal) => {
  if (!audioEl || !room || isLocal) return // never play back local audio
  const participant = typeof room.remoteParticipants?.get === 'function'
    ? room.remoteParticipants.get(identity)
    : room.remoteParticipants?.[identity]
  if (!participant) return

  let targetPub = null
  for (const pub of _iterPubs(participant.audioTrackPublications)) {
    if (pub.track) { targetPub = pub; break }
  }

  const currentSid = audioEl._lkTrackSid || null
  const targetSid = targetPub?.trackSid || null
  if (currentSid === targetSid) return

  if (audioEl._lkTrack) {
    try { audioEl._lkTrack.detach(audioEl) } catch { /* ignore */ }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(audioEl)
      audioEl._lkTrack = targetPub.track
      audioEl._lkTrackSid = targetSid
    } catch (err) { console.warn('[tile] audio attach failed', err) }
  } else {
    audioEl._lkTrack = null
    audioEl._lkTrackSid = null
  }
}

export const MeetParticipantTile = {
  flow: 'y',
  align: 'center center',
  position: 'relative',
  round: 'A',
  overflow: 'hidden',
  background: 'black',
  minHeight: 'F1',
  // Intentionally NO reactive border/transform/outline on the tile root —
  // any reactive prop at this level re-evaluates the whole element on
  // speaker-change events and can cascade into a video re-attach. The
  // speaker ring is an absolute-positioned overlay below, so toggling it
  // only touches that overlay's border, not the tile or the video element.

  onRender: (el, s) => {
    // Re-attach tracks on every render. `track.attach()` is idempotent.
    const room = getLivekitRoom()
    if (!room) return
    const node = el.node
    if (!node) return
    const videoEl = node.querySelector('video')
    const audioEl = node.querySelector('audio')
    _attachVideoTrack(videoEl, room, s.identity, s.isLocal)
    _attachAudioTrack(audioEl, room, s.identity, s.isLocal)
  },

  VideoEl: {
    tag: 'video',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    attr: {
      autoplay: 'autoplay',
      playsinline: 'playsinline',
      // Local video always muted (feedback prevention). Use `s.isLocal`
      // on the tile's own state — `s.parent?.state` was wrong; in smbls
      // child elements inherit the parent's state scope as `s` directly.
      muted: (el, s) => s.isLocal ? 'muted' : null
    }
  },

  AudioEl: {
    tag: 'audio',
    hide: (el, s) => !!s.isLocal,
    attr: { autoplay: 'autoplay' }
  },

  // Camera-off fallback: shows initials on a gray disc. Reads `s.name`
  // and `s.isLocal` directly from the tile's state — not `s.parent`.
  CameraOff: {
    hide: (el, s) => !!s.isCameraEnabled,
    position: 'absolute',
    inset: '0',
    align: 'center center',
    flow: 'y',
    gap: 'Z1',
    color: 'white',
    background: 'black.7',

    InitialsCircle: {
      width: 'D',
      height: 'D',
      round: '100%',
      align: 'center center',
      background: 'gray',
      fontSize: 'B',
      fontWeight: '700',
      color: 'white',
      text: (el, s) => {
        // The participant name is what LiveKit received (which is the
        // user's email for governance users, "Guest Name" for external
        // guests). Resolve via root.people → display name first so
        // initials come out correct for both.
        const people = s.root?.people || []
        const match = people.find(p => p.email === s.name)
        const displayName = match?.name || s.name || s.identity || ''
        const initials = displayName
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(p => p[0])
          .join('')
          .toUpperCase()
        return initials || '?'
      }
    }
  },

  // Active-speaker ring. Absolute-positioned overlay so toggling its
  // border doesn't touch the video element or the tile root — prevents
  // video re-attach on every spoken syllable.
  SpeakerRing: {
    position: 'absolute',
    inset: '0',
    round: 'A',
    pointerEvents: 'none',
    borderStyle: 'solid',
    borderWidth: '3px',
    borderColor: (el, s) =>
      s.root?.livekitSpeakers?.[s.identity] ? 'green' : 'transparent',
    transition: 'border-color .15s linear'
  },

  Footer: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: 'V Z',
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    background: 'black.6',
    color: 'white',
    fontSize: 'Z2',

    Name: {
      tag: 'span',
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        // Resolve to display name via root.people (falls back to the
        // raw participant name which is the email for governance users).
        const people = s.root?.people || []
        const match = people.find(p => p.email === s.name)
        const display = match?.name || s.name || s.identity || ''
        return display + (s.isLocal ? ' (you)' : '')
      }
    },

    MicIcon: {
      tag: 'span',
      fontSize: 'Z',
      text: (el, s) => s.isMicEnabled ? '🎤' : '🚫'
    }
  }
}
